import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Container, Stack } from '@mui/material';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  resendManagerVerificationEmailDocument,
  verifyAndUpdateManagerEmailDocument,
} from '@social-garden/api/documents/manager.ts';
import {
  VerifyEmailForm,
  VerifyEmailSchema,
} from '@/constants/ValidationSchema.ts';
import CodeField from '@/components/CodeField.tsx';
import PageTitle from '@/components/PageTitle.tsx';
import ReadOnlyEmailField from '@/components/ReadOnlyEmailField.tsx';
import { z } from 'zod';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';

const VerifyUpdatedEmailSearchSchema = z.object({
  email: z.string(),
  code: z.string().optional(),
});

export const Route = createFileRoute('/_manager/verify-updated-email')({
  component: VerifyUpdatedEmail,
  validateSearch: (search) => VerifyUpdatedEmailSearchSchema.parse(search),
});

function VerifyUpdatedEmail() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const { t } = useTranslation(['manager']);
  const [
    verifyAndUpdateManagerEmail,
    { loading: verifyAndUpdateManagerEmailLoading },
  ] = useMutation(verifyAndUpdateManagerEmailDocument);
  const [
    resendManagerVerificationEmail,
    { loading: resendManagerVerificationEmailLoading },
  ] = useMutation(resendManagerVerificationEmailDocument);

  const {
    control,
    formState: { dirtyFields, isValid, isSubmitting },
    handleSubmit,
  } = useForm<VerifyEmailForm>({
    resolver: zodResolver(VerifyEmailSchema),
    mode: 'all',
    defaultValues: {
      email: searchParams.email,
      code: searchParams.code,
    },
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting });

  const handleOnResendCode = useCallback(async () => {
    await resendManagerVerificationEmail({
      variables: {
        input: {
          email: searchParams.email,
        },
      },
    });
  }, [resendManagerVerificationEmail, searchParams.email]);

  const handleOnValid = useCallback(
    async ({ email, code }: VerifyEmailForm) => {
      const result = await verifyAndUpdateManagerEmail({
        variables: {
          input: {
            email,
            code,
          },
        },
      });
      if (result.data?.verifyAndUpdateManagerEmail) {
        await navigate({ to: '/settings' });
      }
    },
    [verifyAndUpdateManagerEmail, navigate],
  );

  return (
    <Container
      component="form"
      maxWidth="md"
      noValidate
      onSubmit={handleSubmit(handleOnValid)}>
      <Stack spacing={4}>
        <PageTitle>{t('manager:verifyUpdatedEmail.title')}</PageTitle>
        <Stack spacing={2}>
          <Controller<VerifyEmailForm, 'email'>
            name="email"
            control={control}
            render={ReadOnlyEmailField}
          />
          <Controller<VerifyEmailForm, 'code'>
            name="code"
            control={control}
            render={CodeField}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={verifyAndUpdateManagerEmailLoading}
            disabled={!isValid}>
            {t('manager:verifyUpdatedEmail.submitButton')}
          </Button>
          <Button
            variant="outlined"
            loading={resendManagerVerificationEmailLoading}
            onClick={handleOnResendCode}>
            {t('manager:verifyUpdatedEmail.resendCodeButton')}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
